/**
 * tag-codec — pure codec for the ContentControl tag string.
 *
 * Schema (LOCKED 2026-05-25 per plan §2, §8):
 *
 *   cite|v1|kind=<kind>|cid=<id>|refKey=<encodedRefKey>|status=<status>|
 *     score=<scoreStr>|lang=<lang>|insertedAt=<iso8601>
 *
 * Lives in `src/lib/` (framework-free), consumed by:
 *   - `src/adapters/office/content-control.ts` (encode on insert, decode on list)
 *   - `src/domain/citation-chip/usecases/{encode,decode}-tag.ts` (thin wrappers)
 *
 * S1 PATCH (plan §2.2): `kind` derived from refKey shape on encode.
 * S2 PATCH (plan §2.3): encode rejects refKey > 80 NFC chars.
 */
import {
  deriveChipKind,
  type ChipKind,
  type ChipLanguage,
  type ChipStatus,
  type CitationChip,
} from '@/domain/citation-chip/entity'

/** Grammar constants. */
const PREFIX = 'cite'
const VERSION = 1
const MAX_REFKEY_NFC_CHARS = 80
const MAX_TAG_CHARS = 180

/** Tag field keys, in canonical encoding order. */
const FIELD_KEYS = [
  'kind',
  'cid',
  'refKey',
  'status',
  'score',
  'lang',
  'insertedAt',
] as const

type FieldKey = (typeof FIELD_KEYS)[number]

const KIND_VALUES = new Set<ChipKind>(['article', 'jp', 'piece', 'fait'])
const STATUS_VALUES = new Set<ChipStatus>([
  'pending',
  'verified',
  'unverified',
  'stale',
  'broken-anchor',
])
const LANG_VALUES = new Set<ChipLanguage>(['fr', 'ar', 'en'])

/** TagEncodeError discriminated kind:
 *  - `refkey-too-long`  : refKey NFC > 80 chars (S2 PATCH)
 *  - `tag-too-long`     : encoded tag > 180 chars
 *  - `invalid-field`    : field value violates value-set (kind/status/lang)
 *  - `invalid-score`    : score outside [0,1] or wrong precision */
export type TagEncodeErrorKind =
  | 'refkey-too-long'
  | 'tag-too-long'
  | 'invalid-field'
  | 'invalid-score'

export class TagEncodeError extends Error {
  constructor(
    public readonly kind: TagEncodeErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'TagEncodeError'
  }
}

/** TagDecodeError discriminated kind:
 *  - `wrong_prefix`   : doesn't start with `cite|`
 *  - `wrong_version`  : version is not v1
 *  - `malformed`      : structurally invalid (missing field, bad value) */
export type TagDecodeErrorKind = 'wrong_prefix' | 'wrong_version' | 'malformed'

export class TagDecodeError extends Error {
  constructor(
    public readonly kind: TagDecodeErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'TagDecodeError'
  }
}

/** Encode a CitationChip into its CC tag string. Throws TagEncodeError
 *  on violations. */
export function encode(chip: CitationChip): string {
  // S2 PATCH: defense-in-depth on refKey length.
  const refKeyNfc = chip.refKey.normalize('NFC')
  if (refKeyNfc.length > MAX_REFKEY_NFC_CHARS) {
    throw new TagEncodeError(
      'refkey-too-long',
      `refKey is ${refKeyNfc.length} NFC chars; max ${MAX_REFKEY_NFC_CHARS}`,
    )
  }

  if (!KIND_VALUES.has(chip.kind)) {
    throw new TagEncodeError('invalid-field', `unknown kind: ${chip.kind}`)
  }
  // S1 PATCH (plan §2.2): kind MUST match the refKey-derived shape.
  // Callers may supply chip.kind freely, but encode enforces that it
  // equals the derived value — caller bugs (wrong kind paired with
  // refKey) surface here instead of silently producing wrong-coloured
  // chips down the line.
  let derivedKind: ChipKind
  try {
    derivedKind = deriveChipKind(refKeyNfc)
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    throw new TagEncodeError(
      'invalid-field',
      `refKey shape not derivable to a known kind: ${msg}`,
    )
  }
  if (chip.kind !== derivedKind) {
    throw new TagEncodeError(
      'invalid-field',
      `kind '${chip.kind}' mismatches refKey-derived '${derivedKind}'`,
    )
  }
  if (!STATUS_VALUES.has(chip.status)) {
    throw new TagEncodeError('invalid-field', `unknown status: ${chip.status}`)
  }
  if (!LANG_VALUES.has(chip.language)) {
    throw new TagEncodeError(
      'invalid-field',
      `unknown language: ${chip.language}`,
    )
  }
  if (chip.score < 0 || chip.score > 1) {
    throw new TagEncodeError(
      'invalid-score',
      `score must be in [0,1]: ${chip.score}`,
    )
  }

  const fields: Record<FieldKey, string> = {
    kind: chip.kind,
    cid: chip.cid,
    refKey: encodeURIComponent(refKeyNfc),
    status: chip.status,
    score: formatScore(chip.score),
    lang: chip.language,
    insertedAt: chip.insertedAt,
  }

  const tag = [
    PREFIX,
    `v${VERSION}`,
    ...FIELD_KEYS.map((k) => `${k}=${fields[k]}`),
  ].join('|')

  if (tag.length > MAX_TAG_CHARS) {
    throw new TagEncodeError(
      'tag-too-long',
      `encoded tag is ${tag.length} chars; max ${MAX_TAG_CHARS}`,
    )
  }
  return tag
}

/** Decode a CC tag string into a CitationChip. Throws TagDecodeError
 *  on violations. */
export function decode(tag: string): CitationChip {
  const result = parseTag(tag)
  if (!result.ok) throw result.error
  return result.chip
}

/** Lenient parser — returns a discriminated result rather than
 *  throwing. Used by the adapter to skip malformed tags without
 *  crashing the whole list operation. */
export type ParsedTag =
  | { ok: true; chip: CitationChip }
  | { ok: false; error: TagDecodeError }

export function parseTag(tag: string): ParsedTag {
  const parts = tag.split('|')
  if (parts.length < 2) {
    return {
      ok: false,
      error: new TagDecodeError('wrong_prefix', 'tag too short'),
    }
  }
  if (parts[0] !== PREFIX) {
    return {
      ok: false,
      error: new TagDecodeError(
        'wrong_prefix',
        `expected '${PREFIX}', got '${parts[0]}'`,
      ),
    }
  }
  const versionStr = parts[1]
  if (!/^v\d+$/.test(versionStr)) {
    return {
      ok: false,
      error: new TagDecodeError(
        'wrong_version',
        `expected v<N>, got '${versionStr}'`,
      ),
    }
  }
  const version = parseInt(versionStr.slice(1), 10)
  if (version !== VERSION) {
    return {
      ok: false,
      error: new TagDecodeError(
        'wrong_version',
        `unsupported version v${version}; this codec speaks v${VERSION}`,
      ),
    }
  }

  const kv: Record<string, string> = {}
  for (let i = 2; i < parts.length; i += 1) {
    const segment = parts[i]
    const eq = segment.indexOf('=')
    if (eq < 0) {
      return {
        ok: false,
        error: new TagDecodeError(
          'malformed',
          `missing '=' in segment '${segment}'`,
        ),
      }
    }
    const key = segment.slice(0, eq)
    const value = segment.slice(eq + 1)
    if (!key) {
      return {
        ok: false,
        error: new TagDecodeError('malformed', `empty key in '${segment}'`),
      }
    }
    if (!value) {
      return {
        ok: false,
        error: new TagDecodeError('malformed', `empty value for key '${key}'`),
      }
    }
    kv[key] = value
  }

  for (const required of FIELD_KEYS) {
    if (kv[required] === undefined) {
      return {
        ok: false,
        error: new TagDecodeError('malformed', `missing field '${required}'`),
      }
    }
  }

  const kind = kv.kind as ChipKind
  if (!KIND_VALUES.has(kind)) {
    return {
      ok: false,
      error: new TagDecodeError('malformed', `unknown kind: ${kv.kind}`),
    }
  }
  const status = kv.status as ChipStatus
  if (!STATUS_VALUES.has(status)) {
    return {
      ok: false,
      error: new TagDecodeError('malformed', `unknown status: ${kv.status}`),
    }
  }
  const language = kv.lang as ChipLanguage
  if (!LANG_VALUES.has(language)) {
    return {
      ok: false,
      error: new TagDecodeError('malformed', `unknown lang: ${kv.lang}`),
    }
  }

  // Score must be exactly N.NN (3 chars: digit, dot, 2 digits).
  if (!/^[01]\.\d{2}$/.test(kv.score)) {
    return {
      ok: false,
      error: new TagDecodeError(
        'malformed',
        `score precision must be N.NN: '${kv.score}'`,
      ),
    }
  }
  const score = parseFloat(kv.score)
  if (score < 0 || score > 1) {
    return {
      ok: false,
      error: new TagDecodeError(
        'malformed',
        `score out of range [0,1]: ${score}`,
      ),
    }
  }

  let refKey: string
  try {
    refKey = decodeURIComponent(kv.refKey)
  } catch {
    return {
      ok: false,
      error: new TagDecodeError(
        'malformed',
        `refKey URL-decode failed: '${kv.refKey.slice(0, 20)}'`,
      ),
    }
  }

  // S1 PATCH (plan §2.2): even on decode, kind MUST match the
  // refKey-derived shape. Defends against foreign / corrupted tags
  // where the kind field disagrees with refKey — surfaces as a
  // malformed tag instead of silently rendering a wrong-coloured chip.
  let derivedKind: ChipKind
  try {
    derivedKind = deriveChipKind(refKey)
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    return {
      ok: false,
      error: new TagDecodeError(
        'malformed',
        `refKey shape not derivable to a known kind: ${msg}`,
      ),
    }
  }
  if (kind !== derivedKind) {
    return {
      ok: false,
      error: new TagDecodeError(
        'malformed',
        `kind '${kind}' mismatches refKey-derived '${derivedKind}'`,
      ),
    }
  }

  const chip: CitationChip = {
    cid: kv.cid,
    kind,
    refKey,
    status,
    score,
    language,
    insertedAt: kv.insertedAt,
  }
  return { ok: true, chip }
}

function formatScore(score: number): string {
  return score.toFixed(2)
}

// ─── Cid generation ────────────────────────────────────────────────

/** Crockford-Base32-style alphabet (no 0/O ambiguity, no 1/I). */
const CID_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ'

/** Generate a new citation id. Format: `c-` prefix + 10 alphabet
 *  chars. Total length 12 chars.
 *
 *  Effective entropy is ~45–47 bits: the 30-char alphabet yields
 *  log2(30^10) ≈ 49 bits, slightly reduced by the char-modulo bias on
 *  UUID hex (16 → 30 is non-uniform). Birthday-collision-resistant up
 *  to ~10^7 ids — safe for the per-doc citation envelope (typically
 *  < 500 chips, hard-cap unbounded but Word's CC list stays performant
 *  at that scale). */
export function generateCid(): string {
  const uuid = globalThis.crypto.randomUUID().replace(/-/g, '')
  let acc = ''
  for (let i = 0; i < uuid.length && acc.length < 10; i += 1) {
    const code = uuid.charCodeAt(i)
    acc += CID_ALPHABET[code % CID_ALPHABET.length]
  }
  return `c-${acc}`
}
